import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Loader2 } from 'lucide-react';
import authService from '../../services/api/authService';
import { PasswordStatusResponse } from '../../types';
import { TaskyAlert, TaskyButton, TaskyFormGroup, TaskyInput } from '../tasky';

/**
 * ログイン方法の確認と、パスワードの設定・変更を行うカード。
 *
 * Google ログインで使い始めたユーザーが、ログインしたまま
 * メールアドレス + パスワードのログインに切り替えられるようにする。
 */

/** providerId を画面表示用の名前に変換する */
const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'github.com': 'GitHub',
  'twitter.com': 'X (Twitter)',
  'facebook.com': 'Facebook',
  'apple.com': 'Apple',
  password: 'メールアドレスとパスワード',
  'emailLink': 'メールリンク',
};

const providerLabel = (providerId: string): string =>
  PROVIDER_LABELS[providerId] || providerId;

const CARD =
  'bg-white border border-warm-border rounded-tasky-md shadow-[0_1px_3px_rgba(29,25,39,0.04)]';
const CARD_CONTENT = 'p-6 max-[640px]:p-4';
const SECTION_TITLE = 'text-[16px] font-semibold text-dark m-0 mb-4';
const LABEL = 'block text-[14px] font-medium text-gray-500 mb-2';
const VALUE = 'text-[16px] text-dark m-0 [overflow-wrap:anywhere]';
const DESCRIPTION = 'text-[14px] text-gray-500 leading-relaxed mt-2';

const LoginMethodCard: React.FC = () => {
  const [status, setStatus] = useState<PasswordStatusResponse | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadStatus = useCallback(async () => {
    setIsLoadingStatus(true);
    setLoadError('');
    try {
      setStatus(await authService.getPasswordStatus());
    } catch (e) {
      setLoadError('ログイン方法を取得できませんでした。時間をおいて再度お試しください。');
    } finally {
      setIsLoadingStatus(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const resetForm = () => {
    setCurrentPassword('');
    setPassword('');
    setPasswordConfirmation('');
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSuccessMessage('');

    if (password !== passwordConfirmation) {
      setSubmitError('パスワードの確認が一致しません');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authService.setPassword({
        password,
        password_confirmation: passwordConfirmation,
        ...(status?.has_password ? { current_password: currentPassword } : {}),
      });
      setSuccessMessage(response.message);
      setIsFormOpen(false);
      resetForm();
      await loadStatus();
    } catch (err: any) {
      const data = err?.response?.data;
      // 422 のフィールドエラー（現在のパスワード違い等）を優先して見せる
      const fieldError = data?.errors
        ? (Object.values(data.errors)[0] as string[] | undefined)?.[0]
        : undefined;
      setSubmitError(
        fieldError || data?.message || 'パスワードの設定に失敗しました。時間をおいて再度お試しください。'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasPassword = status?.has_password ?? false;

  return (
    <div className={CARD} style={{ marginTop: 24 }} data-tutorial="account-settings-login-method">
      <div className={CARD_CONTENT}>
        <h2 className={SECTION_TITLE}>ログイン方法</h2>

        {isLoadingStatus ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            読み込み中...
          </div>
        ) : loadError ? (
          <TaskyAlert tone="error">{loadError}</TaskyAlert>
        ) : (
          <>
            <div className="pb-4">
              <label className={LABEL}>現在のログイン方法</label>
              <p className={VALUE}>
                {status && status.providers.length > 0
                  ? status.providers.map(providerLabel).join(' / ')
                  : 'メールアドレスとパスワード'}
              </p>
              {!hasPassword && (
                <p className={DESCRIPTION}>
                  パスワードを設定すると、{status?.email} とパスワードでもログインできるようになります。
                </p>
              )}
            </div>

            {successMessage && (
              <TaskyAlert tone="success" className="mb-4">
                {successMessage}
              </TaskyAlert>
            )}

            {!isFormOpen ? (
              <TaskyButton
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setSuccessMessage('');
                  setIsFormOpen(true);
                }}
              >
                <KeyRound className="w-4 h-4" />
                {hasPassword ? 'パスワードを変更する' : 'パスワードを設定する'}
              </TaskyButton>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5 border-t border-warm-border pt-5">
                {submitError && <TaskyAlert tone="error">{submitError}</TaskyAlert>}

                {hasPassword && (
                  <TaskyFormGroup label="現在のパスワード" required htmlFor="current_password">
                    <TaskyInput
                      id="current_password"
                      name="current_password"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </TaskyFormGroup>
                )}

                <TaskyFormGroup
                  label="新しいパスワード"
                  required
                  htmlFor="new_password"
                  helper="8文字以上"
                >
                  <TaskyInput
                    id="new_password"
                    name="new_password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </TaskyFormGroup>

                <TaskyFormGroup label="新しいパスワード（確認）" required htmlFor="new_password_confirmation">
                  <TaskyInput
                    id="new_password_confirmation"
                    name="new_password_confirmation"
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </TaskyFormGroup>

                <div className="flex gap-3">
                  <TaskyButton type="submit" isLoading={isSubmitting}>
                    {hasPassword ? 'パスワードを変更' : 'パスワードを設定'}
                  </TaskyButton>
                  <TaskyButton
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsFormOpen(false);
                      resetForm();
                    }}
                    disabled={isSubmitting}
                  >
                    キャンセル
                  </TaskyButton>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginMethodCard;
